import { FC, PropsWithChildren, ReactNode, useCallback } from 'react';
import { css } from '@emotion/react';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { CallIntelligenceOrgWetags } from '@wetags/call-intelligence';
import { CallIntelTrialExpired, CallIntelTrialProductTeaser } from '@frontend/call-intel-assets';
import { generateTrackingId, useCallIntelTrialExpiredLocations } from '@frontend/call-intel-core';
import { Header } from '@frontend/header-navigation';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { Page } from '@frontend/page';
import { breakpoints } from '@frontend/responsiveness';
import { useAppScopeStore } from '@frontend/scope';
import { useFeatureDemoModal } from '@frontend/self-upgrade';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { Button, Heading, Text, Chip, MultiLocationSmall } from '@frontend/design-system';
import { CallIntelExpiredTrialNudge } from './call-intel-expired-trial-nudge';
import { ExpiredTrialStatCard } from './expired-trial-stat-card';
import { useCallIntelExpiredTrialStats } from './hooks/use-call-intel-expired-trial-stats';

type CallIntelExpiredTrialGuardProps = {
  demoModalContent?: ReactNode;
  loader?: ReactNode;
  onDemoModalClose?: () => void;
  onDemoModalOpen?: () => void;
};

/**
 * Handles the expired-trial experience for the locations selected in GLS.
 */
export const CallIntelExpiredTrialGuard: FC<PropsWithChildren<CallIntelExpiredTrialGuardProps>> = ({
  children,
  demoModalContent,
  loader = null,
  onDemoModalClose,
  onDemoModalOpen,
}) => {
  const { t } = useTranslation('call-intelligence');
  const { selectedLocationIds } = useAppScopeStore();
  const { navigate } = useSettingsNavigate();

  const [isWetagEnabled, isWetagLoading] = CallIntelligenceOrgWetags.useWetag('call-intel-trial-in-app-nudges');

  const {
    trialExpiredLocationIds,
    trialExpiredDateRangeMap,
    hasActiveTrial,
    hasExpiredTrial,
    hasActiveSubscription,
    isLoading: isTrialAccessLoading,
  } = useCallIntelTrialExpiredLocations({
    locationIds: isWetagEnabled ? selectedLocationIds : [],
  });

  // Show the full expired-trial page only when there is no active Trial or Pro subscription.
  const shouldShowExpiredTrialPage = isWetagEnabled && !hasActiveTrial && !hasActiveSubscription && hasExpiredTrial;

  /**
   * Show the nudge when at least one location is subscribed and another
   * has an expired trial. Do not show it while any trial is still active.
   */
  const shouldShowExpiredTrialNudge = isWetagEnabled && !hasActiveTrial && hasActiveSubscription && hasExpiredTrial;

  // Historical metrics are only requested when the full expired page can be shown.
  const {
    unhappyCalls,
    revenueAtRisk,
    schedulingRate,
    hasNoTrialValue,
    trialDurationDays,
    isLoading: isTrialStatsLoading,
  } = useCallIntelExpiredTrialStats({
    locationIds: shouldShowExpiredTrialPage ? trialExpiredLocationIds : [],
    trialDateRangeMap: trialExpiredDateRangeMap,
  });

  const { openDemoModal, FeatureDemoModal } = useFeatureDemoModal({
    feature: Feature.CALL_INTELLIGENCE,
    locationIds: trialExpiredLocationIds,
    demoModalContent,
    onDemoModalOpen,
    onDemoModalClose,
  });

  const handleSubscribe = useCallback(() => {
    navigate({
      to: '/subscriptions/feature/:id',
      params: { id: 'call-intelligence' },
    });
  }, [navigate]);

  const isTrialStateLoading = isWetagLoading || (isWetagEnabled && isTrialAccessLoading);

  if (isTrialStateLoading) {
    return <>{loader}</>;
  }

  /**
   * Normal, active-trial and mixed states continue to the next layer.
   * Mixed Pro + expired also shows the expired-trial nudge.
   */
  if (!shouldShowExpiredTrialPage) {
    return (
      <>
        {children}

        {shouldShowExpiredTrialNudge && (
          <CallIntelExpiredTrialNudge locationIds={trialExpiredLocationIds} onSubscribe={handleSubscribe} />
        )}
      </>
    );
  }

  // Wait for real historical trial metrics before deciding which expired experience to show.
  if (isTrialStatsLoading) {
    return <>{loader}</>;
  }

  // If the trial produced no value to show, continue to the normal subscription flow.
  if (hasNoTrialValue) {
    return <>{children}</>;
  }

  return (
    <>
      <Page>
        <Page.Header>
          <Page.Header.Heading>
            <Page.Header.Title title={<Header title={t('Call Intelligence')} css={styles.header} />} />
          </Page.Header.Heading>
        </Page.Header>
        <Page.Body>
          <div css={styles.summary}>
            <div css={styles.summaryHeader}>
              <Icon name='weave-upgrade' size={32} />
              <Heading level={3} css={styles.summaryTitle}>
                {trialDurationDays
                  ? t('During your {{trialDurationDays}} day free trial...', {
                      trialDurationDays,
                    })
                  : t('During your free trial...')}
              </Heading>
              {trialExpiredLocationIds.length > 1 && (
                <Chip variant='default' leftElement={<MultiLocationSmall size={16} />}>
                  {t('{{count}} Location', { count: trialExpiredLocationIds.length })}
                </Chip>
              )}
            </div>

            <Heading level={3}>
              {t('You had {{count}} unhappy call and {{revenueAtRisk}} missed scheduling opportunities.', {
                count: unhappyCalls,
                revenueAtRisk,
              })}
            </Heading>

            <div css={styles.stats}>
              <ExpiredTrialStatCard
                title={t('Needs Attention')}
                value={unhappyCalls}
                description={t('Unhappy callers')}
                type='needs-attention'
              />

              <ExpiredTrialStatCard
                title={t('Revenue at Risk')}
                value={revenueAtRisk}
                description={t('Callers still unscheduled')}
                type='revenue-at-risk'
              />

              <ExpiredTrialStatCard
                title={t('Scheduling Rate')}
                value={`${schedulingRate}%`}
                description={t('Scheduled on call')}
                type='scheduling-rate'
              />
            </div>
          </div>
          <div css={styles.dashboardContainer}>
            <div css={styles.expiredDashboard}>
              <CallIntelTrialExpired />
            </div>

            <div css={styles.overlay}>
              <div
                css={styles.subscriptionPrompt}
                role='dialog'
                aria-modal='true'
                aria-labelledby='call-intel-expired-trial-title'
              >
                <CallIntelTrialProductTeaser />

                <div>
                  <Text textAlign='center' weight='bold' id='call-intel-expired-trial-title'>
                    {t('Your insights are no longer available.')}
                  </Text>

                  <Text textAlign='center'>
                    {t(
                      'Subscribe to Call Intelligence to continue improving your patient experience and recovering missed opportunities.'
                    )}
                  </Text>
                </div>

                <div css={styles.actions}>
                  <Button
                    iconName='award-small'
                    onClick={handleSubscribe}
                    trackingId={generateTrackingId({
                      component: 'call-intel-expired-trial-page',
                      context: 'subscribe-button-click',
                    })}
                  >
                    {t('Subscribe')}
                  </Button>

                  <Button
                    variant='tertiary'
                    onClick={openDemoModal}
                    trackingId={generateTrackingId({
                      component: 'call-intel-expired-trial-page',
                      context: 'view-demo-button-click',
                    })}
                  >
                    {t('View Demo')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Page.Body>
      </Page>
      {FeatureDemoModal}
    </>
  );
};

const styles = {
  loader: css`
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  `,
  summary: css`
    display: flex;
    flex-direction: column;
    gap: var(--fabric-space-gap-sm);
    padding: var(--fabric-space-padding-md);
    border: 1px solid var(--fabric-color-accent-seaweed-base);
    border-radius: var(--fabric-number-n-4-px);
    background: var(--fabric-color-accent-seaweed-alpha-subtle);
  `,
  summaryHeader: css`
    display: flex;
    align-items: center;
    gap: var(--fabric-space-gap-xs);
  `,
  summaryTitle: css`
    color: var(--fabric-color-accent-seaweed-strong);
  `,
  header: css`
    line-height: var(--fabric-number-n-10-px);
    > span {
      font-size: var(--fabric-font-size-h1);
    }
  `,
  stats: css`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: var(--fabric-space-gap-sm);
    @container (max-width: ${breakpoints.small.min}px) {
      grid-template-columns: 1fr;
    }
  `,
  dashboardContainer: css`
    position: relative;
    width: 100%;
    margin-top: var(--fabric-space-gap-xl);
  `,
  expiredDashboard: css`
    width: 100%;

    @container (max-width: ${breakpoints.small.min}px) {
      display: none;
    }
  `,
  overlay: css`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    @container (max-width: ${breakpoints.small.min}px) {
      position: static;
    }
  `,
  subscriptionPrompt: css`
    width: min(600px, 100%);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--fabric-space-gap-lg);
    padding: var(--fabric-space-padding-lg);
    border: 1px solid var(--fabric-color-accent-seaweed-base);
    border-radius: var(--fabric-number-n-2-px);
    background: var(--fabric-color-surface-raised);
    box-shadow: var(--fabric-card-shadow);
    @container (max-width: ${breakpoints.small.min}px) {
      width: 100%;
    }
  `,
  actions: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--fabric-space-gap-xs);
  `,
};
